import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authGet, authPost, authPut, authDelete } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '../components/ui/alert-dialog';
import { Progress } from '../components/ui/progress';
import { Target, Plus, Trash, PencilSimple, CalendarDots, TrendUp, Clock, Sparkle, CheckCircle, Warning } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', INR: '₹', JPY: '¥'
};

export default function Goals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addSavingsOpen, setAddSavingsOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingsAmount, setSavingsAmount] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    target_amount: '',
    target_date: '',
    current_amount: '0'
  });

  const currency = user?.currency || 'USD';
  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  const fetchData = useCallback(async () => {
    try {
      const [goalsRes, transRes] = await Promise.all([
        authGet('/api/goals'),
        authGet('/api/transactions?limit=100')
      ]);
      
      setGoals(goalsRes.data);
      setTransactions(transRes.data);
    } catch (e) {
      console.error('Fetch goals error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setForm({ name: '', target_amount: '', target_date: '', current_amount: '0' });
    setEditingGoal(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (goal) => {
    setEditingGoal(goal);
    setForm({
      name: goal.name,
      target_amount: goal.target_amount.toString(),
      target_date: goal.target_date,
      current_amount: (goal.current_amount || 0).toString()
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        name: form.name,
        target_amount: parseFloat(form.target_amount),
        target_date: form.target_date,
        current_amount: parseFloat(form.current_amount) || 0
      };

      if (editingGoal) {
        await authPut(`/api/goals/${editingGoal.id}`, payload);
        toast.success('Goal updated');
      } else {
        await authPost('/api/goals', payload);
        toast.success('Goal created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      console.error('Save goal error:', e);
      const msg = e.response?.data?.detail || e.message || 'Failed to save goal';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    setSubmitting(false);
  };

  const handleAddSavings = async () => {
    if (!selectedGoal || !savingsAmount || parseFloat(savingsAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);

    try {
      await authPut(`/api/goals/${selectedGoal.id}/add-savings`, { amount: parseFloat(savingsAmount) });
      toast.success(`Added ${symbol}${parseFloat(savingsAmount).toFixed(2)} to savings`);
      setAddSavingsOpen(false);
      setSavingsAmount('');
      setSelectedGoal(null);
      fetchData();
    } catch (e) {
      console.error('Add savings error:', e);
      const msg = e.response?.data?.detail || e.message || 'Failed to add savings';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!id) {
      toast.error('Cannot delete: Invalid goal ID');
      return;
    }
    
    const originalGoals = [...goals];
    setGoals(prev => prev.filter(g => g.id !== id));
    setDeleteTarget(null);
    
    try {
      await authDelete(`/api/goals/${id}`);
      console.log('Goal deleted:', id);
      toast.success('Goal deleted');
      fetchData();
    } catch (e) {
      console.error('Delete goal error:', e.response?.data || e.message);
      setGoals(originalGoals);
      const msg = e.response?.data?.detail || e.message || 'Failed to delete goal';
      toast.error(`Delete failed: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
    }
  };

  const formatCurrency = (amount) => {
    return `${symbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate average weekly savings from recent transactions
  const averageWeeklySavings = useMemo(() => {
    const now = new Date();
    const fourWeeksAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);
    
    const recentIncome = transactions
      .filter(t => t.type === 'income' && new Date(t.date) >= fourWeeksAgo)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const recentExpenses = transactions
      .filter(t => t.type === 'expense' && new Date(t.date) >= fourWeeksAgo)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return Math.max(0, (recentIncome - recentExpenses) / 4);
  }, [transactions]);

  // Calculate goal statistics
  const calculateGoalStats = (goal) => {
    const remaining = goal.target_amount - (goal.current_amount || 0);
    const percentage = ((goal.current_amount || 0) / goal.target_amount) * 100;
    
    const targetDate = new Date(goal.target_date);
    const now = new Date();
    const daysRemaining = Math.ceil((targetDate - now) / (24 * 60 * 60 * 1000));
    const weeksRemaining = Math.max(1, Math.ceil(daysRemaining / 7));
    
    const weeklyNeeded = remaining > 0 ? remaining / weeksRemaining : 0;
    
    // Estimated completion date based on current savings rate
    let estimatedCompletionDate = null;
    let weeksToComplete = null;
    if (averageWeeklySavings > 0 && remaining > 0) {
      weeksToComplete = Math.ceil(remaining / averageWeeklySavings);
      estimatedCompletionDate = new Date(now.getTime() + weeksToComplete * 7 * 24 * 60 * 60 * 1000);
    }
    
    const isOnTrack = averageWeeklySavings >= weeklyNeeded;
    const isComplete = (goal.current_amount || 0) >= goal.target_amount;
    const isOverdue = daysRemaining < 0 && !isComplete;
    
    return {
      remaining,
      percentage,
      daysRemaining,
      weeksRemaining,
      weeklyNeeded,
      estimatedCompletionDate,
      weeksToComplete,
      isOnTrack,
      isComplete,
      isOverdue
    };
  };

  // Totals
  const totals = useMemo(() => {
    const totalSaved = goals.reduce((sum, g) => sum + (g.current_amount || 0), 0);
    const totalTarget = goals.reduce((sum, g) => sum + g.target_amount, 0);
    const completedCount = goals.filter(g => (g.current_amount || 0) >= g.target_amount).length;
    return { totalSaved, totalTarget, completedCount, percentage: totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0 };
  }, [goals]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="goals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Savings Goals</h1>
          <p className="text-[#9CA3AF] mt-1">Track your progress towards your goals</p>
        </div>
        
        <Button 
          onClick={openAddDialog}
          className="btn-primary flex items-center gap-2" 
          data-testid="add-goal-btn"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">New Goal</span>
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card-default p-4">
          <p className="text-sm text-[#9CA3AF]">Total Saved</p>
          <p className="text-xl font-semibold text-[#10B981] tabular-nums">
            {formatCurrency(totals.totalSaved)}
          </p>
        </div>
        <div className="card-default p-4">
          <p className="text-sm text-[#9CA3AF]">Total Target</p>
          <p className="text-xl font-semibold text-[#8B5CF6] tabular-nums">
            {formatCurrency(totals.totalTarget)}
          </p>
        </div>
        <div className="card-default p-4">
          <p className="text-sm text-[#9CA3AF]">Overall Progress</p>
          <p className="text-xl font-semibold text-[#3B82F6] tabular-nums">
            {Math.round(totals.percentage)}%
          </p>
        </div>
        <div className="card-default p-4">
          <p className="text-sm text-[#9CA3AF]">Avg. Weekly Savings</p>
          <p className="text-xl font-semibold text-[#F59E0B] tabular-nums">
            {formatCurrency(averageWeeklySavings)}
          </p>
        </div>
      </div>

      {/* Add Savings Dialog */}
      <Dialog open={addSavingsOpen} onOpenChange={setAddSavingsOpen}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#F9FAFB]">
              Add Savings to {selectedGoal?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Amount to Add</Label>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-[#9CA3AF] w-8">{symbol}</span>
                <Input
                  type="number"
                  step="0.01"
                  value={savingsAmount}
                  onChange={(e) => setSavingsAmount(e.target.value)}
                  placeholder="0.00"
                  className="input-default flex-1"
                  data-testid="add-savings-amount"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                type="button" 
                onClick={() => { setAddSavingsOpen(false); setSavingsAmount(''); }}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddSavings}
                disabled={submitting || !savingsAmount}
                className="btn-primary flex-1"
                data-testid="confirm-add-savings"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Goals List */}
      <div className="space-y-4" data-testid="goals-list">
        {goals.length > 0 ? (
          goals.map((goal) => {
            const stats = calculateGoalStats(goal);
            
            let statusColor = '#3B82F6'; // default blue
            let statusBg = 'bg-[#3B82F6]/20';
            let borderColor = 'border-transparent';
            
            if (stats.isComplete) {
              statusColor = '#10B981';
              statusBg = 'bg-[#10B981]/20';
              borderColor = 'border-[#10B981]/30';
            } else if (stats.isOverdue) {
              statusColor = '#EF4444';
              statusBg = 'bg-[#EF4444]/20';
              borderColor = 'border-[#EF4444]/30';
            } else if (!stats.isOnTrack) {
              statusColor = '#F59E0B';
              statusBg = 'bg-[#F59E0B]/20';
              borderColor = 'border-[#F59E0B]/30';
            }

            return (
              <div 
                key={goal.id} 
                className={`card-default p-5 border ${borderColor}`}
                data-testid={`goal-item-${goal.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${statusBg}`}>
                      {stats.isComplete ? (
                        <CheckCircle size={24} weight="duotone" style={{ color: statusColor }} />
                      ) : (
                        <Target size={24} weight="duotone" style={{ color: statusColor }} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-lg">{goal.name}</p>
                        {stats.isComplete && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-[#10B981]/20 text-[#10B981] rounded-full">
                            Completed!
                          </span>
                        )}
                        {stats.isOverdue && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-[#EF4444]/20 text-[#EF4444] rounded-full">
                            Overdue
                          </span>
                        )}
                        {!stats.isComplete && !stats.isOverdue && !stats.isOnTrack && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-[#F59E0B]/20 text-[#F59E0B] rounded-full">
                            Behind pace
                          </span>
                        )}
                        {!stats.isComplete && !stats.isOverdue && stats.isOnTrack && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-[#10B981]/20 text-[#10B981] rounded-full">
                            On track
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                        <CalendarDots size={14} />
                        <span>
                          {stats.isComplete 
                            ? 'Goal achieved!'
                            : stats.daysRemaining > 0 
                              ? `${stats.daysRemaining} days left` 
                              : stats.daysRemaining === 0 
                                ? 'Due today'
                                : `${Math.abs(stats.daysRemaining)} days overdue`
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditDialog(goal)}
                      className="text-[#9CA3AF] hover:text-[#3B82F6] transition-colors p-1"
                      data-testid={`edit-goal-${goal.id}`}
                    >
                      <PencilSimple size={18} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(goal.id)}
                      className="text-[#9CA3AF] hover:text-[#EF4444] transition-colors p-1"
                      data-testid={`delete-goal-${goal.id}`}
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>

                {/* Progress Section */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#9CA3AF]">Progress</span>
                    <span style={{ color: statusColor }} className="font-medium">
                      {Math.round(stats.percentage)}%
                    </span>
                  </div>
                  <div className="relative h-3 bg-[#1F2937] rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(stats.percentage, 100)}%`,
                        backgroundColor: statusColor
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-[#9CA3AF]">
                      {formatCurrency(goal.current_amount || 0)} saved
                    </span>
                    <span className="text-[#F9FAFB] font-medium">
                      {formatCurrency(goal.target_amount)} target
                    </span>
                  </div>
                </div>

                {/* Stats Grid */}
                {!stats.isComplete && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    <div className="card-secondary p-3">
                      <p className="text-xs text-[#9CA3AF]">Remaining</p>
                      <p className="font-semibold tabular-nums" style={{ color: statusColor }}>
                        {formatCurrency(stats.remaining)}
                      </p>
                    </div>
                    <div className="card-secondary p-3">
                      <p className="text-xs text-[#9CA3AF]">Save per week</p>
                      <p className="font-semibold text-[#F59E0B] tabular-nums">
                        {formatCurrency(stats.weeklyNeeded)}
                      </p>
                    </div>
                    {stats.estimatedCompletionDate && (
                      <div className="card-secondary p-3 col-span-2 sm:col-span-1">
                        <p className="text-xs text-[#9CA3AF]">Est. completion</p>
                        <p className="font-semibold text-[#8B5CF6]">
                          {stats.estimatedCompletionDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-[#9CA3AF]">
                          ~{stats.weeksToComplete} weeks at current rate
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Button */}
                <div className="flex items-center justify-between">
                  {!stats.isComplete ? (
                    <>
                      {!stats.isOnTrack && stats.remaining > 0 && (
                        <div className="flex items-center gap-2 text-sm text-[#F59E0B]">
                          <Warning size={16} />
                          <span>Increase savings by {formatCurrency(stats.weeklyNeeded - averageWeeklySavings)}/week</span>
                        </div>
                      )}
                      {stats.isOnTrack && stats.remaining > 0 && (
                        <div className="flex items-center gap-2 text-sm text-[#10B981]">
                          <Sparkle size={16} />
                          <span>You're on track!</span>
                        </div>
                      )}
                      <Button
                        onClick={() => { setSelectedGoal(goal); setAddSavingsOpen(true); }}
                        className="btn-secondary text-sm py-2 px-4 ml-auto"
                        data-testid={`add-savings-${goal.id}`}
                      >
                        <TrendUp size={16} className="mr-1" />
                        Add Savings
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-[#10B981] w-full justify-center py-2">
                      <CheckCircle size={20} weight="duotone" />
                      <span className="font-medium">Congratulations! Goal achieved!</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="card-default p-8 text-center">
            <Target size={48} className="text-[#9CA3AF] mx-auto mb-4" />
            <p className="text-[#9CA3AF]">No savings goals yet</p>
            <p className="text-sm text-[#9CA3AF] mt-1">Create a goal to start saving!</p>
          </div>
        )}
      </div>

      {/* Add/Edit Goal Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#F9FAFB]">
              {editingGoal ? 'Edit Goal' : 'Create Goal'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Goal Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., New Laptop, Emergency Fund"
                className="input-default w-full"
                required
                data-testid="goal-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Target Amount</Label>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-[#9CA3AF] w-8">{symbol}</span>
                <Input
                  type="number"
                  step="0.01"
                  value={form.target_amount}
                  onChange={(e) => setForm(prev => ({ ...prev, target_amount: e.target.value }))}
                  placeholder="0.00"
                  className="input-default flex-1"
                  required
                  data-testid="goal-amount-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Target Date</Label>
              <Input
                type="date"
                value={form.target_date}
                onChange={(e) => setForm(prev => ({ ...prev, target_date: e.target.value }))}
                className="input-default w-full"
                min={new Date().toISOString().split('T')[0]}
                required
                data-testid="goal-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Current Savings</Label>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-[#9CA3AF] w-8">{symbol}</span>
                <Input
                  type="number"
                  step="0.01"
                  value={form.current_amount}
                  onChange={(e) => setForm(prev => ({ ...prev, current_amount: e.target.value }))}
                  placeholder="0.00"
                  className="input-default flex-1"
                  data-testid="goal-current-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                onClick={() => { setDialogOpen(false); resetForm(); }}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !form.name || !form.target_amount || !form.target_date}
                className="btn-ai flex-1"
                data-testid="save-goal-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingGoal ? 'Update' : 'Create Goal')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-[#111827] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F9FAFB]">Delete goal?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              This will permanently remove this savings goal and its progress.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1F2937] border-white/10 text-[#F9FAFB] hover:bg-[#374151]" data-testid="cancel-delete-goal">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#EF4444] text-white hover:bg-[#DC2626]" onClick={() => handleDelete(deleteTarget)} data-testid="confirm-delete-goal">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
